import axiosConfig from './axiosConfig';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createListFetcher } from './listFetchers';
import { queryClient } from './queryClient';

// List events
export const getEventList = createListFetcher('event');

// Create event
export const createEvent = async (eventData) => {
    try {
        const response = await axiosConfig.post('event', eventData);
        return response;
    } catch (error) {
        throw error || 'Failed to create event';
    }
};

// Update event
export const updateEvent = async (id, eventData) => {
    try {
        const response = await axiosConfig.put(`event/${id}`, eventData);
        return response;
    } catch (error) {
        throw error || 'Failed to update event';
    }
};

// Delete event
export const deleteEvent = async (id) => {
    try {
        const response = await axiosConfig.delete(`event/${id}`);
        return response;
    } catch (error) {
        throw error || 'Failed to delete event';
    }
};

// React Query hooks
export const useEventList = (queryParams = {}, options = {}) => useQuery({
    queryKey: ['events', queryParams],
    queryFn: async () => {
        const response = await getEventList(queryParams);
        return response.data?.data ?? [];
    },
    keepPreviousData: true,
    ...options,
});

export const useEventDetails = (id, options = {}) => useQuery({
    queryKey: ['eventDetails', id],
    queryFn: async () => {
        const response = await axiosConfig.get(`event/${id}`);
        return response.data;
    },
    enabled: !!id,
    ...options,
});

export const useCreateEvent = (options = {}) => {
    return useMutation({
        mutationFn: createEvent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
        ...options,
    });
};

export const useUpdateEvent = (options = {}) => {
    return useMutation({
        mutationFn: ({ id, eventData }) => updateEvent(id, eventData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
            if (variables?.id) {
                queryClient.invalidateQueries({ queryKey: ['eventDetails', variables.id] });
            }
        },
        ...options,
    });
};

export const useDeleteEvent = (options = {}) => {
    return useMutation({
        mutationFn: deleteEvent,
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['eventDetails', id] });
        },
        ...options,
    });
};
